<template>
  <div class="seo-site-page" v-loading="pager.loading">
    <div class="seo-site-page__header">
      <div>
        <div class="seo-site-page__title">SEO 站点管理</div>
        <div class="seo-site-page__desc">
          站点级关键词、达标率与排名健康概览
        </div>
      </div>
      <div class="seo-site-page__actions">
        <el-radio-group v-model="range" size="small">
          <el-radio-button
            v-for="item in timeRangeOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="range === 'custom'"
          v-model="customTime"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          size="small"
        ></el-date-picker>
        <el-button size="small" icon="el-icon-refresh" @click="getList">
          刷新
        </el-button>
      </div>
    </div>

    <div class="seo-site-page__scope">
      数据口径：{{
        currentRangeLabel
      }}。当前页面读取本地关键词与最近排名快照，不实时请求 DataForSEO。
    </div>

    <div class="seo-site-page__panel seo-site-page__filter-panel">
      <div class="seo-site-page__filters">
        <el-select
          v-model="params.site_id"
          filterable
          clearable
          size="small"
          placeholder="全部站点"
          class="seo-site-page__filter-site"
        >
          <el-option
            v-for="item in siteOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-input
          v-model="params.site_url"
          clearable
          size="small"
          placeholder="请输入站点URL"
          class="seo-site-page__filter-input"
          @keyup.enter.native="getList(1)"
        ></el-input>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-search"
          @click="getList(1)"
        >
          查询
        </el-button>
        <el-button size="small" @click="resetFilters">重置</el-button>
      </div>
    </div>

    <div class="seo-site-page__stats">
      <div
        v-for="card in metricCards"
        :key="card.key"
        class="seo-site-page__stat-card"
      >
        <div
          :class="[
            'seo-site-page__stat-icon',
            `seo-site-page__stat-icon--${card.tone}`,
          ]"
        >
          <i :class="card.icon"></i>
        </div>
        <div>
          <div class="seo-site-page__stat-label">{{ card.label }}</div>
          <div class="seo-site-page__stat-value">{{ card.value }}</div>
          <div class="seo-site-page__stat-extra">{{ card.extra }}</div>
        </div>
      </div>
    </div>

    <div class="seo-site-page__grid">
      <div class="seo-site-page__panel">
        <div class="seo-site-page__panel-head">
          <div>
            <div class="seo-site-page__panel-title">站点达成率排行</div>
            <div class="seo-site-page__panel-note">按当前站点汇总数据计算</div>
          </div>
        </div>
        <div ref="achievementChart" class="seo-site-page__chart"></div>
      </div>
      <div class="seo-site-page__panel">
        <div class="seo-site-page__panel-head">
          <div>
            <div class="seo-site-page__panel-title">关键词类型分布</div>
            <div class="seo-site-page__panel-note">
              主词、长尾词、品牌词与竞品词
            </div>
          </div>
        </div>
        <div ref="typeChart" class="seo-site-page__chart"></div>
      </div>
    </div>

    <div class="seo-site-page__panel">
      <div class="seo-site-page__panel-head">
        <div>
          <div class="seo-site-page__panel-title">站点数据列表</div>
          <div class="seo-site-page__panel-note">
            站点级 SEO 承诺、关键词结构与达成情况
          </div>
        </div>
      </div>
      <el-table :data="pager.list" empty-text="暂无SEO站点数据">
        <el-table-column
          prop="site_id"
          label="站点ID"
          width="80"
        ></el-table-column>
        <el-table-column
          prop="site_name"
          label="站点"
          min-width="150"
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="site_url"
          label="站点URL"
          min-width="210"
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="committed_keyword_count"
          label="承诺数"
          width="90"
        ></el-table-column>
        <el-table-column
          prop="keyword_count"
          label="关键词数"
          width="100"
        ></el-table-column>
        <el-table-column
          prop="rank_check_count"
          label="参与排名"
          width="100"
        ></el-table-column>
        <el-table-column
          prop="found_count"
          label="已找到"
          width="90"
        ></el-table-column>
        <el-table-column
          prop="qualified_count"
          label="达标数"
          width="90"
        ></el-table-column>
        <el-table-column label="承诺类型" width="100">
          <template v-slot="scope">
            {{ committedRankLimitText(scope.row.committed_rank_limit) }}
          </template>
        </el-table-column>
        <el-table-column label="达成率" width="110">
          <template v-slot="scope">
            <span class="seo-site-page__rate">{{
              scope.row.achievement_rate || "-"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template v-slot="scope">
            <el-button type="text" @click="openDetail(scope.row)"
              >详情</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <div class="seo-site-page__pagination">
        <pagination
          v-model="pager"
          :pageSizes="[10, 20, 30]"
          @change="getList()"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/pagination";
import { Pager } from "@/utils/pager";
import { apiSeoKeywordSiteSummary, apiSeoSiteOptions } from "@/api/seo";
import * as echarts from "echarts/core";
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { BarChart, PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  BarChart,
  PieChart,
  CanvasRenderer,
]);

export default {
  components: {
    Pagination,
  },
  data() {
    return {
      range: "30d",
      customTime: [],
      timeRangeOptions: [
        { label: "今日", value: "today" },
        { label: "近7天", value: "7d" },
        { label: "近30天", value: "30d" },
        { label: "近半年", value: "180d" },
        { label: "近一年", value: "365d" },
        { label: "自定义", value: "custom" },
      ],
      params: {
        site_id: "",
        site_url: "",
      },
      siteOptions: [],
      pager: new Pager(),
      charts: {},
    };
  },
  computed: {
    rows() {
      return this.pager.list || [];
    },
    totals() {
      return this.rows.reduce(
        (acc, item) => {
          acc.site += 1;
          acc.keyword += Number(item.keyword_count || 0);
          acc.rankCheck += Number(item.rank_check_count || 0);
          acc.found += Number(item.found_count || 0);
          acc.qualified += Number(item.qualified_count || 0);
          acc.committed += Number(item.committed_keyword_count || 0);
          acc.core += Number(item.core_count || 0);
          acc.longTail += Number(item.long_tail_count || 0);
          acc.brand += Number(item.brand_count || 0);
          acc.competitor += Number(item.competitor_count || 0);
          return acc;
        },
        {
          site: 0,
          keyword: 0,
          rankCheck: 0,
          found: 0,
          qualified: 0,
          committed: 0,
          core: 0,
          longTail: 0,
          brand: 0,
          competitor: 0,
        }
      );
    },
    averageAchievementRate() {
      if (!this.totals.committed) return "0.00%";
      return `${((this.totals.qualified / this.totals.committed) * 100).toFixed(
        2
      )}%`;
    },
    metricCards() {
      return [
        {
          key: "site",
          label: "SEO站点",
          value: this.totals.site,
          extra: "当前列表站点数",
          icon: "el-icon-monitor",
          tone: "blue",
        },
        {
          key: "keyword",
          label: "关键词总数",
          value: this.totals.keyword,
          extra: "本地关键词库",
          icon: "el-icon-collection-tag",
          tone: "green",
        },
        {
          key: "rank",
          label: "参与排名",
          value: this.totals.rankCheck,
          extra: "启用排名检查",
          icon: "el-icon-data-line",
          tone: "yellow",
        },
        {
          key: "found",
          label: "已找到",
          value: this.totals.found,
          extra: "最近快照命中",
          icon: "el-icon-position",
          tone: "purple",
        },
        {
          key: "qualified",
          label: "达标关键词",
          value: this.totals.qualified,
          extra: "满足承诺排名",
          icon: "el-icon-success",
          tone: "red",
        },
        {
          key: "rate",
          label: "平均达成率",
          value: this.averageAchievementRate,
          extra: "达标 / 承诺",
          icon: "el-icon-pie-chart",
          tone: "cyan",
        },
      ];
    },
    currentRangeLabel() {
      if (
        this.range === "custom" &&
        this.customTime &&
        this.customTime.length === 2
      ) {
        return `${this.customTime[0]} 至 ${this.customTime[1]}`;
      }
      const item = this.timeRangeOptions.find(
        (option) => option.value === this.range
      );
      return item ? item.label : "近30天";
    },
  },
  watch: {
    rows() {
      this.$nextTick(this.renderCharts);
    },
  },
  created() {
    this.getSiteOptions();
    this.getList();
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    Object.values(this.charts).forEach((chart) => chart.dispose());
  },
  methods: {
    getSiteOptions() {
      apiSeoSiteOptions().then((resp) => {
        this.siteOptions = resp || [];
      });
    },
    getList(page) {
      page && (this.pager.page = page);
      this.pager.request({
        callback: apiSeoKeywordSiteSummary,
        params: { ...this.params },
      });
    },
    resetFilters() {
      this.params.site_id = "";
      this.params.site_url = "";
      this.getList(1);
    },
    openDetail(row) {
      this.$router.push(`/seo/sites/detail/${row.site_id}`);
    },
    chart(ref) {
      if (!this.$refs[ref]) return null;
      if (!this.charts[ref]) {
        this.charts[ref] = echarts.init(this.$refs[ref]);
      }
      return this.charts[ref];
    },
    renderCharts() {
      this.renderAchievementChart();
      this.renderTypeChart();
      this.resizeCharts();
    },
    renderAchievementChart() {
      const chart = this.chart("achievementChart");
      if (!chart) return;
      const rows = this.rows
        .slice()
        .sort((a, b) => this.rateValue(b) - this.rateValue(a));
      if (!rows.length) {
        chart.setOption(this.emptyChartOption("暂无站点达成率数据"), true);
        return;
      }
      chart.setOption(
        {
          color: ["#1a73e8"],
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: (items) => {
              const item = items[0];
              return `${item.name}<br/>达成率：${item.value.toFixed(2)}%`;
            },
          },
          grid: { top: 16, left: 12, right: 18, bottom: 8, containLabel: true },
          xAxis: {
            type: "value",
            max: 100,
            axisLabel: { color: "#5f6368", formatter: "{value}%" },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: "#edf0f5" } },
          },
          yAxis: {
            type: "category",
            inverse: true,
            data: rows.map((item) => item.site_name || `站点${item.site_id}`),
            axisLabel: { color: "#3c4043", width: 120, overflow: "truncate" },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          series: [
            {
              type: "bar",
              data: rows.map((item) => this.rateValue(item)),
              barWidth: 18,
              itemStyle: { borderRadius: [0, 8, 8, 0] },
            },
          ],
        },
        true
      );
    },
    renderTypeChart() {
      const chart = this.chart("typeChart");
      if (!chart) return;
      const data = [
        { name: "主词", value: this.totals.core },
        { name: "长尾词", value: this.totals.longTail },
        { name: "品牌词", value: this.totals.brand },
        { name: "竞品词", value: this.totals.competitor },
      ].filter((item) => item.value > 0);
      if (!data.length) {
        chart.setOption(this.emptyChartOption("暂无关键词类型数据"), true);
        return;
      }
      chart.setOption(
        {
          color: ["#1a73e8", "#34a853", "#fbbc04", "#ea4335"],
          tooltip: { trigger: "item" },
          legend: {
            bottom: 0,
            icon: "circle",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { color: "#5f6368" },
          },
          series: [
            {
              type: "pie",
              radius: ["48%", "72%"],
              center: ["50%", "44%"],
              itemStyle: { borderColor: "#fff", borderWidth: 3 },
              label: { color: "#3c4043" },
              data,
            },
          ],
        },
        true
      );
    },
    emptyChartOption(text) {
      return {
        graphic: {
          type: "text",
          left: "center",
          top: "middle",
          style: { text, fill: "#9aa0a6", fontSize: 13 },
        },
      };
    },
    resizeCharts() {
      Object.values(this.charts).forEach((chart) => chart.resize());
    },
    rateValue(row) {
      const committed = Number(row.committed_keyword_count || 0);
      if (!committed) return 0;
      return Math.min(
        100,
        (Number(row.qualified_count || 0) / committed) * 100
      );
    },
    committedRankLimitText(value) {
      const limit = Number(value || 30);
      return limit === 10 ? "前10" : limit === 50 ? "前50" : "前30";
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-site-page {
  min-height: calc(100vh - 84px);
  margin: -20px;
  padding: 24px;
  background: #f8fafc;
  color: #202124;

  &__header,
  &__panel,
  &__stat-card {
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;
  }

  &__desc,
  &__panel-note,
  &__stat-extra {
    color: #5f6368;
    font-size: 13px;
    line-height: 20px;
  }

  &__actions,
  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  &__scope {
    margin-top: 12px;
    padding: 10px 14px;
    color: #5f6368;
    font-size: 13px;
    background: #eef4ff;
    border: 1px solid #d9e7ff;
    border-radius: 8px;
  }

  &__panel {
    margin-top: 16px;
    padding: 18px;
  }

  &__filter-panel {
    padding: 14px 16px;
  }

  &__filter-site,
  &__filter-input {
    width: 220px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px;
    margin-top: 16px;
  }

  &__stat-card {
    display: flex;
    gap: 12px;
    min-height: 108px;
    padding: 16px;
  }

  &__stat-icon {
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 8px;
    font-size: 18px;

    &--blue {
      background: #1a73e8;
    }

    &--green {
      background: #34a853;
    }

    &--yellow {
      background: #fbbc04;
      color: #202124;
    }

    &--purple {
      background: #9334e6;
    }

    &--red {
      background: #ea4335;
    }

    &--cyan {
      background: #12b5cb;
    }
  }

  &__stat-label {
    color: #5f6368;
    font-size: 13px;
    line-height: 18px;
  }

  &__stat-value {
    margin-top: 6px;
    font-size: 24px;
    font-weight: 700;
    line-height: 30px;
  }

  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
    gap: 16px;
  }

  &__panel-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__panel-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__chart {
    height: 320px;
  }

  &__rate {
    color: #1a73e8;
    font-weight: 700;
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  ::v-deep .el-table th.el-table__cell {
    background: #f8fafc;
    color: #5f6368;
    font-weight: 600;
  }
}

@media (max-width: 1440px) {
  .seo-site-page {
    &__stats {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

@media (max-width: 1100px) {
  .seo-site-page {
    &__header,
    &__grid {
      grid-template-columns: 1fr;
    }

    &__header {
      flex-direction: column;
    }
  }
}
</style>
